import React, { useCallback, useContext, useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { TextBoxConfigProps, TextBoxConfigValue } from './types';
import LibraryTitleInput from '../library-title-input';
import CustomTooltip from '../../Tooltip/CustomTooltip';
import { NewCheckbox } from '../../PIM/common/NewCheckbox';
import { NewInput } from '../../PIM/common/NewInput';
import FieldSetupPanelContext from './field-setup-panel.context';
import TextOptions from './text-options';
import type { TextOptionsPatch } from './text-options.types';

/**
 * TextBoxConfig component for configuring text input form controls in the system canvas.
 * Allows users to set up text box properties, required field settings, and save configurations to library.
 * @param value - Current text box configuration value
 * @param onChange - Callback function called when configuration changes
 * @param className - Optional CSS class name for styling
 * @param disabled - Whether the configuration controls should be disabled
 * @param showLibraryTitle - Whether to show the library title input section
 */
const TextBoxConfig: React.FC<TextBoxConfigProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  showLibraryTitle = true,
  width,
  onWidthChange,
  widthError,
  hideTitle = false,
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const setupPanel = useContext(FieldSetupPanelContext);
  const isInSetupCard = setupPanel !== null;
  const showOptionsPart = setupPanel !== 'setup';
  const titlePlaceholder = t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER');
  const showWidthRow = onWidthChange != null;

  const [localValue, setLocalValue] = useState<TextBoxConfigValue>(value);
  const [libraryTitle, setLibraryTitle] = useState<string>('');

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const updateTitle = useCallback(
    (next: string) => {
      setLocalValue((prev) => {
        const nextVal = { ...prev, title: next };
        onChange(nextVal);
        return nextVal;
      });
    },
    [onChange],
  );

  const toggleRequired = useCallback(() => {
    setLocalValue((prev) => {
      const nextVal = { ...prev, required: !prev.required };
      onChange(nextVal);
      return nextVal;
    });
  }, [onChange]);

  const handleLibrarySave = useCallback(() => {
    setLibraryTitle('');
  }, []);

  const handleLibrarySelect = useCallback(
    (controlTypeData: any) => {
      const fetchedConfig = controlTypeData?.content?.content as TextBoxConfigValue | undefined;
      if (fetchedConfig) {
        setLocalValue(fetchedConfig);
        onChange(fetchedConfig);
      }
    },
    [onChange],
  );

  const titleBlock = (
    <div className="mb-2">
      <p className="m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROLS.TEXTBOX.TITLE')}</p>
      <NewInput
        type="text"
        placeholder={titlePlaceholder}
        value={localValue.title}
        onChange={(e) => updateTitle(e.target.value)}
        disabled={disabled}
      />
    </div>
  );

  /**
   * Applies a Character Limit or placeholder change from the Options tab.
   * @param patch - The changed option.
   */
  const updateTextOptions = (patch: TextOptionsPatch) => {
    const nextVal = { ...localValue, ...patch };
    setLocalValue(nextVal);
    onChange(nextVal);
  };

  return (
    <div className={`cm-maxw-520 ${className}`}>
      {showWidthRow && (
        <div className="mb-2">
          <label className="f-12 d-block">Width (%)</label>
          <NewInput
            type="text"
            placeholder="e.g. 25% or 150px"
            value={width ?? ''}
            onChange={(e) => onWidthChange(e.target.value.trim())}
            title="Field width when rendered (e.g. 25% or 150px)"
            invalid={!!widthError}
          />
          {widthError && <div className="invalid-feedback d-block">{widthError}</div>}
        </div>
      )}
      {showWidthRow && titleBlock}
      {showLibraryTitle && (
        <LibraryTitleInput
          value={libraryTitle}
          onChange={setLibraryTitle}
          onSave={handleLibrarySave}
          onSelect={handleLibrarySelect}
          controlType="text"
          contentData={localValue}
          placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
        />
      )}
      {!isInSetupCard && (
      <div className="mb-1 mt-2 d-flex align-items-center cm-checkbox">
        <label className="m-0 mr-1">{t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')}</label>
        {!localValue.title?.trim() ? (
          <CustomTooltip tooltipText={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_CHECKBOX_TOOLTIP')}>
            <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled || !localValue.title?.trim()} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
          </CustomTooltip>
        ) : (
          <NewCheckbox className="mb-1 mr-0" checked={!!localValue.required} onChange={toggleRequired} disabled={disabled} aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.REQUIRED_FIELD')} />
        )}
      </div>
      )}
      {!showWidthRow && !hideTitle && titleBlock}
      {showOptionsPart && (
        <TextOptions
          characterLimit={localValue.characterLimit ?? null}
          placeholder={localValue.placeholder ?? ''}
          placeholderLabel={t('CUSTOM_MODULE.SYSTEM_CANVAS.PLACEHOLDER_TEXT')}
          disabled={disabled}
          onChange={updateTextOptions}
        />
      )}
    </div>
  );
};

export default memo(TextBoxConfig);
